import { useState, useMemo } from 'react';
import { useStore } from 'hooks/useStore';
import { OOCTabType, FCColorType, OOCForecastAdjustment } from '../oocUpdated.type';

export const TAB_LABELS: Record<OOCTabType, string> = {
  MAS: 'MAS DP',
  NC: 'NC DP',
  ProvMAS: 'Prov MAS',
  ProvNC: 'Prov NC',
};

export const getCellClass = (colorType: FCColorType, styles: Record<string, string>): string => {
  switch (colorType) {
    case 'new':
      return styles.cellNew;
    case 'modified':
      return styles.cellModified;
    case 'removed':
      return styles.cellRemoved;
    default:
      return styles.cellUnchanged;
  }
};

export const useOOCSCReviewLogic = () => {
  const { OOCStoreUpdated: OOCStore } = useStore();
  const { currentSubmission, navigateToDashboard } = OOCStore;

  const [activeTab, setActiveTab] = useState<OOCTabType>('MAS');

  const tabGroups = useMemo(() => {
    if (!currentSubmission) return {} as Partial<Record<OOCTabType, OOCForecastAdjustment[]>>;
    const groups: Partial<Record<OOCTabType, OOCForecastAdjustment[]>> = {};
    (['MAS', 'NC', 'ProvMAS', 'ProvNC'] as OOCTabType[]).forEach((tab) => {
      const filtered = currentSubmission.adjustments.filter((a) => a.tab === tab);
      if (filtered.length > 0) groups[tab] = filtered;
    });
    return groups;
  }, [currentSubmission]);

  const availableTabs = Object.keys(tabGroups) as OOCTabType[];
  const currentActiveTab = availableTabs.includes(activeTab) ? activeTab : availableTabs[0] ?? 'MAS';

  const bulkChangeCount = useMemo(() => {
    if (!currentSubmission) return 0;
    return currentSubmission.adjustments.filter((adj) =>
      adj.forecasts.some(
        (f) =>
          f.previousValue !== null &&
          f.previousValue !== 0 &&
          f.value !== null &&
          Math.abs((f.value - f.previousValue) / f.previousValue) >= 0.2
      )
    ).length;
  }, [currentSubmission]);

  const feedbackDeadline = useMemo(() => {
    if (!currentSubmission?.communicatedDate) return 'N/A';
    const commDate = new Date(currentSubmission.communicatedDate);
    commDate.setHours(commDate.getHours() + 48);
    return commDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }, [currentSubmission]);

  return {
    currentSubmission,
    navigateToDashboard,
    activeTab,
    setActiveTab,
    tabGroups,
    availableTabs,
    currentActiveTab,
    bulkChangeCount,
    feedbackDeadline,
  };
};
